import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Apollo, QueryRef } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DialogEmailComponent } from 'src/app/features/dialog/dialog-email/dialog-email.component';
import { ManifestService } from '../../documents/manifest.service';
// import { DialogEmailComponent } from "src/app/features/dialog/dialog-email/dialog-email.component";
import {
	AddEmailOutDocument,
	AddEmailTemplateLocationContactDocument,
	AvailabilityType,
	CopyEmailTemplateDocument,
	DeleteEmailTemplateDocument,
	EmailAddressInput,
	EmailInput,
	EmailTemplateType,
	Flight,
	GetAllEmailTemplatesDocument,
	GetEmailTemplateByIdDocument,
	GetEmailTemplateByLanguageDocument,
	GetEmailTemplateDocument,
	GetEmailTemplatesDocument,
	TenantCurrency,
} from '../../graphql/generated/gen-types';
import { SnackBar } from '../../utility/snackBar';
import { UserService } from '../admin/user.service';
import { FlightService } from '../flight/flight.service';
import { TenantSettingService } from '../tenantSetting/tenant-setting.service';
import { TranslateCustomService } from '../../utility/translate-custom.service';
import { FileService } from 'src/app/core/services/file/file.service';
import { DialogEmailMultipleComponent } from 'src/app/features/dialog/dialog-email-multiple/dialog-email-multiple.component';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(timezone);
dayjs.extend(utc);

@Injectable({
	providedIn: 'root',
})
export class EmailService {
	userProfile;
	tenantEmailFrom;
	tenantEmailReplyTo;
	tenantEmailAccountant: EmailAddressInput;
	timezone: string;
	currency: TenantCurrency;

	constructor(
		private apollo: Apollo,
		private dialog: MatDialog,
		private translateCustom: TranslateCustomService,
		private tenantSettingService: TenantSettingService,
		private userService: UserService,
		private snackBar: SnackBar,
		private manifestService: ManifestService,
		private flightService: FlightService,
		private fileService: FileService,
	) {
		this.tenantSettingService.getTenantSetting().valueChanges.subscribe((tenantSettingResult) => {
			this.tenantEmailFrom = tenantSettingResult.data.tenantSetting.mailFrom;
			this.tenantEmailReplyTo = tenantSettingResult.data.tenantSetting.mailReplyTo;
			if (tenantSettingResult.data.tenantSetting.mailAccountant) {
				this.tenantEmailAccountant = {
					email: tenantSettingResult.data.tenantSetting?.mailAccountant?.email,
					name: tenantSettingResult.data.tenantSetting?.mailAccountant?.name,
				};
			}
			if (tenantSettingResult.data.tenantSetting.operatorSettings?.timezone) {
				this.currency = tenantSettingResult.data.tenantSetting.operatorSettings?.currency;
				this.timezone = tenantSettingResult.data.tenantSetting.operatorSettings?.timezone;
				if (!this.timezone) {
					this.timezone = 'Europe/Brussels';
				}
			}
		});

		this.userService.userProfile.subscribe((userProfile) => {
			this.userProfile = userProfile;
		});
	}

	public getAllEmailTemplates(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetAllEmailTemplatesDocument,
		});
	}

	private getEmailTemplate(type: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetEmailTemplateDocument,
			variables: {
				type,
			},
		});
	}
	private getEmailTemplateByID(id: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetEmailTemplateByIdDocument,
			variables: {
				id,
			},
		});
	}
	public copyEmailTemplate(id: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: CopyEmailTemplateDocument,
				variables: {
					emailTemplateId: id,
				},
				refetchQueries: [GetAllEmailTemplatesDocument],
			})
			.pipe(map((result: any) => result.data));
	}
	public addEmailTemplateLocationContact(): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: AddEmailTemplateLocationContactDocument,
				refetchQueries: [GetAllEmailTemplatesDocument],
			})
			.pipe(map((result: any) => result.data));
	}

	public deleteEmailTemplate(id: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeleteEmailTemplateDocument,
				variables: {
					emailTemplateId: id,
				},
				refetchQueries: [GetAllEmailTemplatesDocument],
			})
			.pipe(map((result: any) => result.data));
	}

	private getEmailTemplates(type: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetEmailTemplatesDocument,
			variables: {
				type,
			},
		});
	}

	private getEmailTemplateByLanguage(type: string, lang: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetEmailTemplateByLanguageDocument,
			variables: {
				type,
				lang,
			},
		});
	}

	public addEmailOut(emailInput: EmailInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: AddEmailOutDocument,
				variables: {
					emailInput,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	private sendMail(emails: any[]) {
		if (emails.length === 0) return;
		const dialogRef = this.dialog.open(DialogEmailComponent, {
			width: window.innerWidth <= 360 ? '95%' : '80%',
			maxWidth: 'none',

			data: {
				emails: emails,
				readOnly: false,
			},
		});
	}

	private sendMailMultiple(emails: any[], templates: string[]) {
		if (emails.length === 0) return;
		const dialogRef = this.dialog.open(DialogEmailMultipleComponent, {
			width: window.innerWidth <= 360 ? '95%' : '80%',
			maxWidth: 'none',
			data: {
				emails,
				templates,
				readOnly: false,
			},
		});
	}

	public sendBookingConfirmationMail(booking?: any, flight?: any, lang?) {
		let language = booking.contactLanguage;
		if (lang) language = lang;
		this.getEmailTemplate(EmailTemplateType.BookingConfirmEmail)
			.valueChanges.pipe(take(1))
			.subscribe({
				next: (result) => {
					const emailTemplate = result.data.getEmailTemplate.template[language];
					// parse the template with the context
					const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, booking, flight);
					const emailBody = this.parseEmailTemplate(emailTemplate.body, language, booking, flight);
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;
					const attachments = emailTemplate.attachments;

					const emailInput = {
						bookingId: booking.id,
						crewId: null,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to: booking.contactEmail,
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments,
					};

					// get all destination addresses
					const emails = [emailInput];

					// send the emails
					this.sendMail(emails);
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
	}

	public sendCrewLoginMail(crew: any) {
		const language = crew.language;
		this.getEmailTemplateByLanguage(EmailTemplateType.CrewOtpEmail, language)
			.valueChanges.pipe(take(1))
			.subscribe({
				next: (result) => {
					const emailTemplate = result.data.getEmailTemplateByLanguage;
					// copy the template (no parsing needed)
					const emailSubject = emailTemplate.subject;
					let emailBody = emailTemplate.body;
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;

					const emailInput = {
						bookingId: null,
						crewId: crew.id,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to: crew.emailNotifications,
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments: [],
					};

					// get all destination addresses
					const emails = [emailInput];

					// send the emails
					this.sendMail(emails);
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
	}

	public sendFlightBookingsMail(language, flight?: any) {
		this.getEmailTemplate(EmailTemplateType.BookingFlightConfirmEmail)
			.valueChanges.pipe(take(1))
			.subscribe({
				next: (result) => {
					const emailTemplate = result.data.getEmailTemplate.template[language];
					const emails = [];
					// parse the template with the context
					flight?.bookings.list
						.filter((b) => b.contactLanguage === language)
						.forEach((booking) => {
							const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, booking, flight);
							const emailBody = this.parseEmailTemplate(emailTemplate.body, language, booking, flight);
							const emailFrom = this.tenantEmailFrom;
							const emailReplyTo = this.tenantEmailReplyTo;

							const emailInput = {
								bookingId: booking.id,
								crewId: null,
								from: {
									email: emailFrom.email,
									name: emailFrom.name,
								},
								replyTo: {
									email: emailReplyTo.email,
									name: emailReplyTo.name,
								},
								to: booking.contactEmail,
								subject: emailSubject,
								text: emailBody,
								html: emailBody,
								attachments: [],
							};

							emails.push(emailInput);
						});

					// send the emails
					this.sendMail(emails);
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
	}

	public sendFlightBookingsMailMultiple(flight?: any) {
		// get the email templates of type BookingFlightConfirmEmail
		const sub = this.getEmailTemplates(EmailTemplateType.BookingFlightConfirmEmail)
			.valueChanges.pipe(take(1))
			.subscribe({
				next: (result) => {
					const templates = result.data.getEmailTemplates.map((t) => t.title);
					const emails = [];
					// for each title
					result.data.getEmailTemplates.forEach((emailTemplate) => {
						// parse the template with the context
						flight?.bookings.list.forEach((booking) => {
							const language = booking.contactLanguage;
							// default en template
							const template = emailTemplate.template[language] ? emailTemplate.template[language] : emailTemplate.template['en'];
							const emailSubject = this.parseEmailTemplate(template.subject, language, booking, flight);
							const emailBody = this.parseEmailTemplate(template.body, language, booking, flight);
							const emailAttachments = template.attachments;
							const emailFrom = this.tenantEmailFrom;
							const emailReplyTo = this.tenantEmailReplyTo;

							const emailInput = {
								title: emailTemplate.title,
								bookingId: booking.id,
								crewId: null,
								from: {
									email: emailFrom.email,
									name: emailFrom.name,
								},
								replyTo: {
									email: emailReplyTo.email,
									name: emailReplyTo.name,
								},
								to: booking.contactEmail,
								contactName: booking.contactName,
								language,
								subject: emailSubject,
								text: emailBody,
								html: emailBody,
								attachments: emailAttachments,
							};

							emails.push(emailInput);
						});
					});

					// send the emails
					this.sendMailMultiple(emails, templates);
					sub.unsubscribe();
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
		return;
	}

	public sendFlightLocationMail(flight: any) {
		const language = flight.location.contact.language ? flight.location.contact.language : 'nl';
		this.getEmailTemplateByID(flight.location.contact.emailTemplate)
			.valueChanges.pipe(take(1))
			.subscribe({
				next: (result) => {
					const emailTemplate = result.data.getEmailTemplateByID.template[language];
					const emails = [];
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;
					const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, null, flight);
					const emailBody = this.parseEmailTemplate(emailTemplate.body, language, null, flight);
					const emailAttachments = emailTemplate.attachments;
					// parse the template with the context

					const emailInput = {
						flightId: flight.id,
						locationId: flight.location.id,
						bookingId: null,
						crewId: null,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to: flight.location.contact.email,
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments: emailAttachments,
					};

					emails.push(emailInput);

					// send the emails
					this.sendMail(emails);
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
	}

	public sendFlightEndMail(language, flight?: any) {
		this.getEmailTemplate(EmailTemplateType.BookingFlightCancelEmail)
			.valueChanges.pipe(take(1))
			.subscribe({
				next: (result) => {
					const emailTemplate = result.data.getEmailTemplate.template[language];
					const emails = [];
					// parse the template with the context
					flight?.bookings.list
						.filter((b) => b.contactLanguage === language)
						.forEach((booking) => {
							const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, booking, flight);
							const emailBody = this.parseEmailTemplate(emailTemplate.body, language, booking, flight);
							const emailFrom = this.tenantEmailFrom;
							const emailReplyTo = this.tenantEmailReplyTo;

							const emailInput = {
								bookingId: booking.id,
								crewId: null,
								from: {
									email: emailFrom.email,
									name: emailFrom.name,
								},
								replyTo: {
									email: emailReplyTo.email,
									name: emailReplyTo.name,
								},
								to: booking.contactEmail,
								subject: emailSubject,
								text: emailBody,
								html: emailBody,
								attachments: [],
							};

							emails.push(emailInput);
						});

					// send the emails
					this.sendMail(emails);
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
	}

	public sendFlightPilotMail(flight?: any) {
		if (!flight?.pilot) return;
		const language = flight.pilot.crew.language;

		this.getEmailTemplate(EmailTemplateType.PilotFlightEmail)
			.valueChanges.pipe(take(1))
			.subscribe({
				next: (result) => {
					const emailTemplate = result.data.getEmailTemplate.template[language];
					const emails = [];
					// parse the template with the context
					const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, null, flight);
					const emailBody = this.parseEmailTemplate(emailTemplate.body, language, null, flight);
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;

					const emailInput = {
						bookingId: null,
						crewId: flight.pilot.crew.id,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to: flight.pilot.crew.emailNotifications ?? flight.pilot.crew.email,
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments: [],
					};

					emails.push(emailInput);

					// send the emails
					this.sendMail(emails);
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
	}

	public sendFlightCrewMail(language, flight?: any) {
		this.getEmailTemplate(EmailTemplateType.CrewFlightEmail)
			.valueChanges.pipe(take(1))
			.subscribe({
				next: (result) => {
					const emailTemplate = result.data.getEmailTemplate.template[language];
					const emails = [];
					// parse the template with the context
					const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, null, flight);
					const emailBody = this.parseEmailTemplate(emailTemplate.body, language, null, flight);
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;

					flight?.groundCrews
						.filter((c) => c.crew.language === language)
						.forEach((gc) => {
							const emailInput = {
								bookingId: null,
								crewId: gc.crew.id,
								from: {
									email: emailFrom.email,
									name: emailFrom.name,
								},
								replyTo: {
									email: emailReplyTo.email,
									name: emailReplyTo.name,
								},
								to: gc.crew.emailNotifications,
								subject: emailSubject,
								text: emailBody,
								html: emailBody,
								attachments: [],
							};

							emails.push(emailInput);
						});

					// send the emails
					this.sendMail(emails);
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
	}

	public sendFlightPaymentDetails(flightId: string) {
		const emails = [];

		let crewId = null;
		if (this.userProfile.crew?.id) crewId = this.userProfile.crew.id;

		// send to operator or to crewNotification address
		let to = this.tenantEmailFrom.email;
		if (this.userProfile.crew?.emailNotifications) to = this.userProfile.crew?.emailNotifications;

		// generate document
		let attachments = [];
		this.flightService.get(flightId).valueChanges.subscribe(
			async (result) => {
				const paymentPDF = await this.manifestService.generatePaymentPDF('all', result.data.flight, false, false, this.currency);
				const file = new File([paymentPDF.pdf as BlobPart], paymentPDF.title);

				const uploadFile = this.fileService.uploadFile(file);
				uploadFile.uploadTask.then((res) => {
					attachments.push({ filename: paymentPDF.title, reference: res.metadata.fullPath });
					// parse the template with the context
					const emailSubject = paymentPDF.title;
					const emailBody = 'This is an auto-generated e-mail, please do not reply';
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;

					const emailInput = {
						bookingId: null,
						crewId,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to,
						cc: this.tenantEmailAccountant ? [this.tenantEmailAccountant] : [],
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments,
					};
					this.addEmailOut(emailInput).subscribe();
				});
			},
			(err) => {
				this.snackBar.openSnackBarError(err.message);
			},
		);
	}

	public parseEmailTemplate(emailTemplate: string, language: string, booking?: any, flight?: Flight) {
		const reHour = /{{ flight_hour }}/gi;
		const reHourMeeting = /{{ flight_meeting_hour }}/gi;
		const rePeriod = /{{ flight_period }}/gi;
		const reDate = /{{ flight_date }}/gi;
		const reBalloon = /{{ flight_balloon }}/gi;
		const reCity = /{{ location_city }}/gi;
		const reLocationName = /{{ location_name }}/gi;
		const reMeetingPoint = /{{ meeting_point }}/gi;
		const reAddress = /{{ location_address }}/gi;
		const reLocation = /{{ location_description }}/gi;
		const reBookingCode = /{{ booking_code }}/gi;
		const reBookingName = /{{ booking_name }}/gi;
		const reBookingComment = /{{ booking_comment }}/gi;
		const reBookingPassengers = /{{ booking_passengers }}/gi;
		const reBookingAdults = /{{ booking_adults }}/gi;
		const reBookingChilds = /{{ booking_childs }}/gi;
		const reBookingSpecial = /{{ booking_special }}/gi;
		const reBookingWeight = /{{ booking_weight }}/gi;
		const reBookingRegion = /{{ booking_region }}/gi;
		// const reBookingType = /{{ booking_type }}/gi;
		const reBookingAvailabilityDate = /{{ booking_availability_date }}/gi;
		const reBookingTotalAmount = /{{ booking_total_amount }}/gi;
		const reBookingOpenAmount = /{{ booking_open_amount }}/gi;
		const reBookingUpURL = /{{ booking_up_url }}/gi;
		const rePilotName = /{{ pilot_name }}/gi;
		const reCrewNames = /{{ crew_names }}/gi;

		// replace the variables with example text
		let emailText = emailTemplate ? emailTemplate : '';

		if (!flight) {
			const unknown = this.translateCustom.translate('FLIGHTS.unknown', language);
			emailText = emailText.replace(reHour, unknown);
			emailText = emailText.replace(reHourMeeting, unknown);
			emailText = emailText.replace(reDate, unknown);
			emailText = emailText.replace(reBalloon, unknown);
			emailText = emailText.replace(rePeriod, unknown);
			emailText = emailText.replace(reCity, unknown);
			emailText = emailText.replace(reLocationName, unknown);
			emailText = emailText.replace(reMeetingPoint, unknown);
			emailText = emailText.replace(reAddress, unknown);
			emailText = emailText.replace(reLocation, unknown);
			emailText = emailText.replace(rePilotName, unknown);
			emailText = emailText.replace(reCrewNames, unknown);
		} else {
			// Assuming `language` is a variable that holds the locale string (e.g., 'en')
			dayjs.locale(language);
			const localMoment = dayjs(flight?.hour).tz(this.timezone);
			const localMomentMeeting = dayjs(flight?.hourMeeting).tz(this.timezone);
			emailText = emailText.replace(reHour, localMoment.format('HH:mm'));
			emailText = emailText.replace(reHourMeeting, localMomentMeeting.format('HH:mm'));
			emailText = emailText.replace(reDate, localMoment.format('dddd DD MMMM YYYY'));
			emailText = emailText.replace(reBalloon, flight?.balloon?.name);
			emailText = emailText.replace(rePeriod, this.translateCustom.translate('FLIGHTS.' + flight?.period, language));
			emailText = emailText.replace(reCity, flight?.location?.city ? flight?.location?.city : '');
			emailText = emailText.replace(reLocationName, flight?.location?.name);
			emailText = emailText.replace(reMeetingPoint, flight?.location?.meetingPointAddress);
			emailText = emailText.replace(reAddress, flight?.location?.address ? flight?.location?.address : '');
			emailText = emailText.replace(reLocation, flight?.location?.description ? flight?.location?.description : '');
			emailText = emailText.replace(rePilotName, flight?.pilot?.crew?.name ? flight?.pilot?.crew?.name : '');
			emailText = emailText.replace(reCrewNames, flight?.groundCrews?.map((c) => c.crew.name).join(', '));
		}

		if (booking) {
			let localBookingAvailabilityDate = this.translateCustom.translate('BOOKINGS.' + booking.availabilityType, language);
			if (booking && booking.availabilityType === AvailabilityType.Date && booking.preferredFlights?.length > 0) {
				localBookingAvailabilityDate += dayjs(booking.preferredFlights[0].date).locale(language).format('dddd DD MMMM YYYY');
			}
			emailText = emailText.replace(reBookingCode, booking.bookingCode);
			emailText = emailText.replace(reBookingName, booking.contactName);
			emailText = emailText.replace(reBookingComment, booking.comments ? booking.comments : '');
			emailText = emailText.replace(
				reBookingAdults,
				(booking.passengerCounters.passengerCount - booking.passengerCounters.childCount).toString(),
			);
			emailText = emailText.replace(reBookingPassengers, booking.passengerCounters?.passengerCount.toString());
			emailText = emailText.replace(reBookingChilds, booking.passengerCounters?.childCount.toString());
			emailText = emailText.replace(reBookingSpecial, booking.passengerCounters?.disabilityCount.toString());
			emailText = emailText.replace(reBookingWeight, booking.passengerCounters?.totalWeight.toString());
			emailText = emailText.replace(reBookingRegion, booking.region?.name);
			emailText = emailText.replace(reBookingAvailabilityDate, localBookingAvailabilityDate);
			emailText = emailText.replace(reBookingTotalAmount, booking.passengerCounters?.totalAmount.toString());
			emailText = emailText.replace(reBookingOpenAmount, booking.passengerCounters?.openAmount.toString());
			emailText = emailText.replace(reBookingUpURL, booking.upUrl);
			// emailText = emailText.replace(reBookingType, this.translateCustom.translate('BOOKINGS.' + booking.bookingType, language));
		}

		return emailText;
	}
}
